import { Err, Ok, Result } from 'ts-res';
import { CacheManager } from './cacheManager';
import {
  ClientConfiguration,
  FeatureVariableType,
  JSONType,
  RuleType,
} from './clientConfiguration';
import { ClientSettings } from './clientSettings';
import {
  CACHE_CLEANUP_TIMEOUT,
  DEFAULT_VISITOR_DATA_FILTERS,
  IN_HOLDOUT_VARIATION_KEY,
  OFF_VARIATION_KEY,
} from './constants';
import {
  Conversion,
  CustomData,
  DataManager,
  KameleoonDataType,
} from './kameleoonData';
import { WAREHOUSE_AUDIENCE_KEY } from './constants';
import { KameleoonError, KameleoonException } from './kameleoonError';
import { Requester, UrlProvider } from './requester';
import {
  ClientDataType,
  KameleoonStorageKey,
  VariationDataType,
  DataStorage,
  ConsentDataType,
  IStorage,
  DataInfoType,
  TrackingDataType,
  StorageCleanupManager,
} from './storage';
import {
  TrackConversionParamsType,
  FeatureFlagType,
  GetFeatureFlagVariableParamsType,
  FeatureVariableResultType,
  SDKCoreParameters,
  TrackingCacheItemType,
  RemoteVisitorDataParamsType,
  GetVisitorWarehouseAudienceParamsType,
  SetUserConsentParametersType,
  FeatureFlagVariableType,
  KameleoonVariationType,
  GetFeatureVariablesParamsType,
  VariableType,
  VariationType,
  KameleoonVariableType,
  GetFeatureVariationParametersType,
  GetVariationParamsType,
  GetVariationsParamsType,
  IsFeatureFlagActiveParamsType,
  TrackingStatus,
  SetForcedVariationParametersType,
  EvaluatedExperimentType,
  EvaluateParametersType,
  FlushParamsType,
} from './types';
import { ExternalPackageInfoType } from './sdkInfoTypes';
import { IVisitorCodeManager } from './visitorCodeManager';
import { Parser, Utilities } from './utilities';
import {
  FeatureFlagVariationType,
  VariationConfiguration,
} from './variationConfiguration';
import { IKameleoonClient } from './kameleoonClientInterface';
import { ConditionFactory, TargetingDataType } from './targeting';
import { KameleoonEventSource } from './eventSource';
import { DEFAULT_MAX_AGE, PATH, ZERO_MAX_AGE } from './visitorCodeManager';
import { EventCallbackType, EventManager, EventType } from './eventManager';
import { Tracker } from './tracking';
import { KameleoonLogger, LogLevel } from 'src/logging';
import {
  ForcedExperimentVariationDataType,
  ForcedFeatureVariationDataType,
  ForcedVariationRuleType,
  ForcedVariationVarByExpType,
  ForcedExperimentVariationType,
  ForcedFeatureVariationType,
} from 'src/storage/types';
import { Hasher } from 'src/hasher';
import { FeatureFlagType as ConfigFeatureFlagType } from './clientConfiguration/types';
import { KameleoonVisitorDataType } from 'src/kameleoonData/types';
import { ConditionFactoryInstance } from 'src/targeting/conditions/conditionFactoryInstance';
import { TargetedSegment } from 'src/kameleoonData/targetedSegment';

/**
 * @class
 * KameleoonClient - a class for creating kameleoon client instance
 * for communicating with Kameleoon JavaScript SDK Core, when new instance is created
 */
export class KameleoonClient implements IKameleoonClient {
  private variationConfiguration: VariationConfiguration;
  private clientConfiguration: ClientConfiguration;
  private dataManager: DataManager;
  private consentDataStorage: IStorage<ConsentDataType>;
  private trackingCache: CacheManager<TrackingCacheItemType>;
  private requester: Requester;
  private externalPackageInfo: ExternalPackageInfoType;
  protected initialized: boolean = false;
  private visitorCodeManager: IVisitorCodeManager;
  private eventManager: EventManager;
  protected readonly tracker: Tracker;
  private environment: string;
  protected readonly stubMode?: boolean;

  /**
   * @param {SDKCoreParameters} sdkCoreParameters - parameters for initializing sdk core
   */
  constructor({
    siteCode,
    dependencies,
    configuration,
    internalConfiguration,
    stubMode,
  }: SDKCoreParameters) {
    this.stubMode = stubMode;
    if (stubMode) {
      this.variationConfiguration = {} as any;
      this.clientConfiguration = {} as any;
      this.dataManager = {} as any;
      this.consentDataStorage = {} as any;
      this.trackingCache = {} as any;
      this.requester = {} as any;
      this.externalPackageInfo = {} as any;
      this.visitorCodeManager = {} as any;
      this.eventManager = {} as any;
      this.tracker = {} as any;
      this.environment = '';
      return;
    }
    const logger = internalConfiguration?.externals.externalLogger;
    if (logger) {
      KameleoonLogger.setLogger(logger);
    }
    ConditionFactoryInstance.setConditionFactoryInstance(
      new ConditionFactory(),
    );
    KameleoonLogger.debug`CALL: new KameleoonClient(siteCode: ${siteCode}, dependencies: ${dependencies}, configuration: ${configuration})`;

    const { settings: clientSettings } = new ClientSettings(
      siteCode,
      configuration,
    );
    this.environment = clientSettings.environment;
    const {
      externalStorage,
      externalEventSource,
      externalPackageInfo,
      externalVisitorCodeManager,
      externalRequester,
      externalPRNG,
      customDataManager,
    } = internalConfiguration.externals;

    const { useAbortController } = internalConfiguration.settings;
    const trackingCache = new CacheManager<TrackingCacheItemType>(
      CACHE_CLEANUP_TIMEOUT,
    );

    const clientDataStorage = new DataStorage<ClientDataType>(
      externalStorage,
      KameleoonStorageKey.ClientData,
    );

    const variationDataStorage = new DataStorage<VariationDataType>(
      externalStorage,
      KameleoonStorageKey.VariationData,
    );
    const forcedVariationDataStorage =
      new DataStorage<ForcedExperimentVariationDataType>(
        externalStorage,
        KameleoonStorageKey.ForcedExperimentVariation,
      );
    const simulatedVariationDataStorage =
      new DataStorage<ForcedFeatureVariationDataType>(
        externalStorage,
        KameleoonStorageKey.ForcedFeatureVariation,
      );
    const consentDataStorage = new DataStorage<ConsentDataType>(
      externalStorage,
      KameleoonStorageKey.ConsentData,
    );
    const trackingStorage = new DataStorage<TrackingDataType>(
      externalStorage,
      KameleoonStorageKey.TrackingData,
    );

    let dataManager = customDataManager;
    if (!dataManager) {
      const targetingDataStorage = new DataStorage<TargetingDataType>(
        externalStorage,
        KameleoonStorageKey.TargetingData,
      );
      const dataInfoStorage = new DataStorage<DataInfoType>(
        externalStorage,
        KameleoonStorageKey.DataInfo,
      );

      dataManager = new DataManager({
        dataStorage: targetingDataStorage,
        infoStorage: dataInfoStorage,
        cleanupInterval: clientSettings.cleanupInterval,
        packageInfo: externalPackageInfo,
      });
    }

    const urlProvider = dependencies?.urlProvider ?? new UrlProvider();

    urlProvider.initialize({
      domain: clientSettings.networkDomain,
      siteCode: clientSettings.siteCode,
      packageInfo: externalPackageInfo,
      environment: clientSettings.environment,
    });

    const requester = new Requester({
      urlProvider,
      useAbortController,
      packageInfo: externalPackageInfo,
      externalRequester: externalRequester,
      requestTimeout: clientSettings.requestTimeout,
      trackRetryDelay: clientSettings.trackRetryDelay,
    });

    const eventSource = new KameleoonEventSource({
      siteCode: clientSettings.siteCode,
      externalEventSource,
      urlProvider,
    });

    const eventManager = new EventManager();

    const clientConfiguration = new ClientConfiguration({
      urlProvider,
      storage: clientDataStorage,
      updateInterval: clientSettings.updateInterval,
      dataManager,
      eventManager,
      requester,
      eventSource,
      externalVisitorCodeManager,
      externalPackageInfo,
      defaultDataFile: configuration?.defaultDataFile,
    });

    const variationConfiguration = new VariationConfiguration(
      variationDataStorage,
      forcedVariationDataStorage,
      simulatedVariationDataStorage,
      externalVisitorCodeManager,
      clientConfiguration,
    );

    const tracker = new Tracker({
      dataManager,
      trackingStorage,
      variationConfiguration,
      requester,
      trackingInterval: clientSettings.trackingInterval,
      prng: externalPRNG,
      clientConfiguration,
    });

    this.tracker = tracker;
    this.variationConfiguration = variationConfiguration;
    this.requester = requester;
    this.trackingCache = trackingCache;
    this.dataManager = dataManager;
    this.clientConfiguration = clientConfiguration;
    this.externalPackageInfo = externalPackageInfo;
    this.consentDataStorage = consentDataStorage;
    this.visitorCodeManager = externalVisitorCodeManager;
    this.eventManager = eventManager;

    if (clientSettings.cleanupInterval !== null) {
      new StorageCleanupManager(
        externalStorage,
        clientSettings.cleanupInterval,
      );
    }
    KameleoonLogger.debug`RETURN: new KameleoonClient(siteCode: ${siteCode}, dependencies: ${dependencies}, configuration: ${configuration})`;
  }

  public async initialize(): Promise<boolean> {
    if (this.stubMode) {
      return false;
    }
    KameleoonLogger.info('CALL: KameleoonClient.initialize()');
    const result = await this.clientConfiguration.initialize();
    result.throw();

    this.initialized = true;

    KameleoonLogger.info`RETURN: KameleoonClient.initialize() -> (result: ${result.ok})`;
    return result.ok;
  }

  public addData(visitorCode: string, ...data: KameleoonDataType[]): void {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.addData(visitorCode: ${visitorCode}, data: ${data})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    if (!this.initialized) {
      throw new KameleoonError(KameleoonException.Initialization);
    }

    this.dataManager.storeData(visitorCode, ...data).throw();
    KameleoonLogger.info`RETURN: KameleoonClient.addData(visitorCode: ${visitorCode}, data: ${data})`;
  }

  public async getRemoteVisitorData({
    visitorCode,
    shouldAddData = true,
    filters = DEFAULT_VISITOR_DATA_FILTERS,
  }: RemoteVisitorDataParamsType): Promise<KameleoonDataType[]> {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getRemoteVisitorData(visitorCode: ${visitorCode}, shouldAddData: ${shouldAddData}, filters: ${filters})`;
    if (!this.initialized) {
      throw new KameleoonError(KameleoonException.Initialization);
    }

    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const { previousVisitAmount } = filters;

    if (
      typeof previousVisitAmount === 'number' &&
      (previousVisitAmount < 1 || previousVisitAmount > 25)
    ) {
      throw new KameleoonError(KameleoonException.VisitAmount);
    }

    const isUniqueIdentifier = this.dataManager.isUniqueIdentifier(visitorCode);

    const result = await this.requester.getVisitorData({
      visitorCode,
      filters,
      isMappingIdentifier: isUniqueIdentifier,
    });
    const data = result.throw();

    const { visitorData, storageVisitorData, visitsData, mappingIdentifier } =
      Parser.parseVisitorData({
        data,
        filters,
        visitorCode,
        dataManager: this.dataManager,
        variationConfiguration: this.variationConfiguration,
      });

    for (const dataItem of storageVisitorData) {
      if (shouldAddData || this.dataManager.isPersistentCustomData(dataItem)) {
        this.addData(visitorCode, dataItem);
      }
    }

    // --- Note ---
    // Data on visits amount and time stamps is not returned to a user
    if (visitsData && shouldAddData) {
      this.addData(visitorCode, visitsData);
    }

    if (filters?.visitorCode && mappingIdentifier) {
      this.dataManager.setMappingIdentifier(visitorCode, mappingIdentifier);
    }

    KameleoonLogger.info`RETURN: KameleoonClient.getRemoteVisitorData(visitorCode: ${visitorCode}, shouldAddData: ${shouldAddData}, filters: ${filters}) -> (visitorData: ${visitorData})`;
    return visitorData;
  }

  public trackConversion({
    visitorCode,
    goalId,
    negative,
    revenue,
    metadata,
  }: TrackConversionParamsType): void {
    if (this.stubMode) {
      return;
    }
    KameleoonLogger.info`CALL: KameleoonClient.trackConversion(visitorCode: ${visitorCode}, goalId: ${goalId}, negative: ${negative}, revenue: ${revenue}, metadata: ${metadata})`;
    this.addData(
      visitorCode,
      new Conversion({ goalId, revenue, negative, metadata }),
    );
    this.flush(visitorCode);
    KameleoonLogger.info`RETURN: KameleoonClient.trackConversion(visitorCode: ${visitorCode}, goalId: ${goalId}, negative: ${negative}, revenue: ${revenue}, metadata: ${metadata})`;
  }

  public flush(params?: string | FlushParamsType): void {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    let visitorCode: string | undefined;
    let isInstant: boolean = false;
    if (typeof params === 'string') {
      visitorCode = params;
    } else if (params) {
      visitorCode = params.visitorCode;
      isInstant = params.instant ?? false;
    }

    KameleoonLogger.info`CALL: KameleoonClient.flush(visitorCode: ${visitorCode}, instant: ${isInstant})`;
    if (!this.initialized) {
      throw new KameleoonError(KameleoonException.Initialization);
    }

    if (typeof visitorCode === 'string') {
      this.visitorCodeManager.validateVisitorCode(visitorCode).throw();
      if (isInstant) {
        this.tracker.sendDataForVisitor(
          visitorCode,
          this._isConsentProvided(visitorCode),
        );
      } else {
        this.tracker.scheduleVisitor(
          visitorCode,
          this._isConsentProvided(visitorCode),
        );
      }
    } else {
      for (const visitorCodeKey of this.dataManager.unsentDataVisitors) {
        this.tracker.scheduleVisitor(
          visitorCodeKey,
          this._isConsentProvided(visitorCodeKey),
        );
      }
      for (const visitorCodeKey of this.variationConfiguration.getVisitorsWithUnsentData()) {
        this.tracker.scheduleVisitor(
          visitorCodeKey,
          this._isConsentProvided(visitorCodeKey),
        );
      }
      if (isInstant) {
        this.tracker.sendDataAll();
      }
    }

    KameleoonLogger.info`RETURN: KameleoonClient.flush(visitorCode: ${visitorCode}, instant: ${isInstant})`;
  }

  public getFeatureFlags(): FeatureFlagType[] {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info('CALL: KameleoonClient.getFeatureFlags()');
    if (!this.initialized) {
      throw new KameleoonError(KameleoonException.Initialization);
    }

    const featureFlags = this.clientConfiguration.featureFlags;
    const resultFeatureFlags: FeatureFlagType[] = [];

    featureFlags.forEach(({ id, featureKey }) => {
      resultFeatureFlags.push({
        id,
        key: featureKey,
      });
    });

    KameleoonLogger.info`RETURN: KameleoonClient.getFeatureFlags() -> (FeatureFlags: ${resultFeatureFlags})`;
    return resultFeatureFlags;
  }

  public getVisitorFeatureFlags(visitorCode: string): FeatureFlagType[] {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getVisitorFeatureFlags(visitorCode: ${visitorCode})`;
    if (!this.initialized) {
      throw new KameleoonError(KameleoonException.Initialization);
    }

    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const activeVariations = this._getActiveFeatureVariations(visitorCode);
    const resultFeatureFlags: FeatureFlagType[] = [];

    activeVariations.forEach(({ featureFlagId, featureKey }) => {
      resultFeatureFlags.push({
        id: featureFlagId,
        key: featureKey,
      });
    });

    KameleoonLogger.info`RETURN: KameleoonClient.getVisitorFeatureFlags(visitorCode: ${visitorCode}) -> (FeatureFlags: ${resultFeatureFlags})`;
    return resultFeatureFlags;
  }

  public getVariation({
    visitorCode,
    featureKey,
    track = true,
  }: GetVariationParamsType): VariationType {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey}, track: ${track})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const variation = this._getFeatureVariation({
      visitorCode,
      featureKey,
      track,
    }).throw();

    KameleoonLogger.info`RETURN: KameleoonClient.getVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey}, track: ${track}) -> (variation: ${variation})`;
    return variation;
  }

  public getVariations({
    visitorCode,
    onlyActive = false,
    track = true,
  }: GetVariationsParamsType): Map<string, VariationType> {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getVariations(visitorCode: ${visitorCode}, onlyActive: ${onlyActive}, track: ${track})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const variations: Map<string, VariationType> = new Map();
    const featureFlags = this.clientConfiguration.featureFlags;

    for (const featureFlag of featureFlags.values()) {
      const variation = this._getFeatureVariation({
        visitorCode,
        featureKey: featureFlag.featureKey,
        track,
      });

      if (variation.ok) {
        if (!onlyActive || variation.data.key !== OFF_VARIATION_KEY) {
          variations.set(featureFlag.featureKey, variation.data);
        }
      }
    }

    KameleoonLogger.info`RETURN: KameleoonClient.getVariations(visitorCode: ${visitorCode}, onlyActive: ${onlyActive}, track: ${track}) -> (variations: ${variations})`;

    return variations;
  }

  public getActiveFeatureFlags(
    visitorCode: string,
  ): Map<string, KameleoonVariationType> {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getActiveFeatureFlags(visitorCode: ${visitorCode})`;

    if (!this.initialized) {
      throw new KameleoonError(KameleoonException.Initialization);
    }

    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const activeVariations = this._getActiveFeatureVariations(visitorCode);
    const resultFeatureList: Map<string, KameleoonVariationType> = new Map();

    activeVariations.forEach((variation) => {
      const { variationKey, featureKey, variationId, experimentId } = variation;

      let variables: FeatureVariableType[] = [];

      if (variationId !== null && experimentId !== null) {
        variables = this._getFeatureVariables({
          visitorCode,
          featureKey,
          variationKey,
        });
      }

      const resultVariables = variables.map((variable) =>
        Parser.parseFeatureVariable(variable).throw(),
      );

      resultFeatureList.set(variation.featureKey, {
        key: variationKey,
        id: variationId,
        experimentId,
        variables: resultVariables,
      });
    });

    KameleoonLogger.info`RETURN: KameleoonClient.getActiveFeatureFlags(visitorCode: ${visitorCode}) -> (FeatureFlags: ${resultFeatureList})`;

    return resultFeatureList;
  }

  public getFeatureFlagVariationKey(
    visitorCode: string,
    featureKey: string,
  ): string {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getFeatureFlagVariationKey(visitorCode: ${visitorCode}, featureKey: ${featureKey})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const { key } = this._getFeatureVariation({
      visitorCode,
      featureKey,
      track: true,
    }).throw();

    KameleoonLogger.info`RETURN: KameleoonClient.getFeatureFlagVariationKey(visitorCode: ${visitorCode}, featureKey: ${featureKey}) -> (variationKey: ${key})`;
    return key;
  }

  public getFeatureFlagVariable({
    visitorCode,
    featureKey,
    variableKey,
  }: GetFeatureFlagVariableParamsType): FeatureFlagVariableType {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getFeatureFlagVariable(visitorCode: ${visitorCode}, featureKey: ${featureKey}, variableKey: ${variableKey})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const { key: variationKey } = this._getFeatureVariation({
      visitorCode,
      featureKey,
      track: true,
    }).throw();

    const variables = this._getFeatureVariables({
      visitorCode,
      featureKey,
      variationKey,
    });
    const variable = variables.find((item) => item.key === variableKey);

    if (!variable) {
      throw new KameleoonError(
        KameleoonException.FeatureFlagVariableNotFound,
        variableKey,
        visitorCode,
      );
    }

    const { type, value } = Parser.parseFeatureVariable(variable).throw();

    const featureFlagVariable = { type, value } as FeatureFlagVariableType;
    KameleoonLogger.info`RETURN: KameleoonClient.getFeatureFlagVariable(visitorCode: ${visitorCode}, featureKey: ${featureKey}, variableKey: ${variableKey}) -> (FeatureFlagVariable: ${featureFlagVariable})`;
    return featureFlagVariable;
  }

  public getFeatureFlagVariables(
    visitorCode: string,
    featureKey: string,
  ): FeatureVariableResultType[] {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.getFeatureFlagVariables(visitorCode: ${visitorCode}, featureKey: ${featureKey})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const { key: variationKey } = this._getFeatureVariation({
      visitorCode,
      featureKey,
      track: true,
    }).throw();

    const variables = this._getFeatureVariables({
      visitorCode,
      featureKey,
      variationKey,
    });
    const resultVariables: FeatureVariableResultType[] = [];

    for (const variable of variables) {
      const parsedVariable = Parser.parseFeatureVariable(variable).throw();
      resultVariables.push(parsedVariable);
    }

    KameleoonLogger.info`RETURN: KameleoonClient.getFeatureFlagVariables(visitorCode: ${visitorCode}, featureKey: ${featureKey}) -> (FeatureFlagVariables: ${resultVariables})`;
    return resultVariables;
  }

  public isFeatureFlagActive(visitorCode: string, featureKey: string): boolean;
  public isFeatureFlagActive({
    visitorCode,
    featureKey,
    track,
  }: IsFeatureFlagActiveParamsType): boolean;
  public isFeatureFlagActive(
    firstParam: string | IsFeatureFlagActiveParamsType,
    secondParam?: string,
  ): boolean {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    let [visitorCode, featureKey, track] = ['', '', true];

    if (typeof firstParam === 'string') {
      visitorCode = firstParam;
      featureKey = secondParam!;
    } else {
      [visitorCode, featureKey] = [
        firstParam.visitorCode,
        firstParam.featureKey,
      ];

      if (firstParam.track !== undefined) {
        track = firstParam.track;
      }
    }
    KameleoonLogger.info`CALL: KameleoonClient.isFeatureFlagActive(visitorCode: ${visitorCode}, featureKey: ${featureKey}, track ${track})`;

    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    try {
      const { key } = this._getFeatureVariation({
        visitorCode,
        featureKey,
        track,
      }).throw();

      const isActive = key !== OFF_VARIATION_KEY;

      KameleoonLogger.info`RETURN: KameleoonClient.isFeatureFlagActive(visitorCode: ${visitorCode}, featureKey: ${featureKey}, track: ${track}) -> (isActive: ${isActive})`;
      return isActive;
    } catch (err) {
      if (
        err instanceof KameleoonError &&
        err.type === KameleoonException.FeatureFlagEnvironmentDisabled
      ) {
        KameleoonLogger.info`RETURN: KameleoonClient.isFeatureFlagActive(visitorCode: ${visitorCode}, featureKey: ${featureKey}, track: ${track}) -> (isActive: false)`;
        return false;
      }

      throw err;
    }
  }

  public async getRemoteData(key: string): Promise<JSONType> {
    if (this.stubMode) {
      return null;
    }
    KameleoonLogger.info`CALL: KameleoonClient.getRemoteData(key: ${key})`;
    const remoteData = await this.requester.getRemoteData(key);

    KameleoonLogger.info`RETURN: KameleoonClient.getRemoteData(key: ${key}) -> (remoteData: ${remoteData})`;
    return remoteData.throw();
  }

  public async getVisitorWarehouseAudience({
    visitorCode,
    customDataIndex,
    warehouseKey,
  }: GetVisitorWarehouseAudienceParamsType): Promise<CustomData | null> {
    if (this.stubMode) {
      return null;
    }
    KameleoonLogger.info`CALL: KameleoonClient.getVisitorWarehouseAudience(visitorCode: ${visitorCode}, customDataIndex: ${customDataIndex}, warehouseKey: ${warehouseKey})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const data = await this.getRemoteData(warehouseKey || visitorCode);

    if (!data) {
      throw new KameleoonError(
        KameleoonException.RemoteData,
        `No data found for ${warehouseKey || visitorCode} key`,
      );
    }

    if (
      !data[WAREHOUSE_AUDIENCE_KEY] ||
      Object.keys(data[WAREHOUSE_AUDIENCE_KEY]).length === 0
    ) {
      KameleoonLogger.info`RETURN: KameleoonClient.getVisitorWarehouseAudience(visitorCode: ${visitorCode}, customDataIndex: ${customDataIndex}, warehouseKey: ${warehouseKey}) -> (customData: null)`;
      return null;
    }

    const customData = new CustomData(
      customDataIndex,
      ...Object.keys(data[WAREHOUSE_AUDIENCE_KEY]),
    );

    this.dataManager.storeData(visitorCode, customData);

    KameleoonLogger.info`RETURN: KameleoonClient.getVisitorWarehouseAudience(visitorCode: ${visitorCode}, customDataIndex: ${customDataIndex}, warehouseKey: ${warehouseKey}) -> (customData: ${customData})`;
    return customData;
  }

  public onEvent<E extends EventType>(
    event: E,
    callback: EventCallbackType<E>,
  ): void {
    if (this.stubMode) {
      throw new KameleoonError(KameleoonException.Initialization);
    }
    KameleoonLogger.info`CALL: KameleoonClient.onEvent(event: ${event}, callback)`;
    if (!this.initialized) {
      throw new KameleoonError(KameleoonException.Initialization);
    }

    this.eventManager.addEventHandler(event, callback);
    KameleoonLogger.info`RETURN: KameleoonClient.onEvent(event: ${event}, callback)`;
  }

  public getEngineTrackingCode(visitorCode: string): string {
    if (this.stubMode) {
      return '';
    }
    KameleoonLogger.info`CALL: KameleoonClient.getEngineTrackingCode(visitorCode: ${visitorCode})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const trackingCode = Utilities.getTrackingCode(
      this.trackingCache,
      visitorCode,
    );
    KameleoonLogger.info`RETURN: KameleoonClient.getEngineTrackingCode(visitorCode: ${visitorCode}) -> (trackingCode: ${trackingCode})`;
    return trackingCode;
  }

  public isInitialized(): boolean {
    KameleoonLogger.debug`CALL/RETURN: KameleoonClient.isInitialized() -> (initialized: ${this.initialized})`;
    return this.initialized;
  }

  public setLogLevel(level: LogLevel): void {
    KameleoonLogger.setLogLevel(level);
  }

  public setForcedVariation({
    visitorCode,
    experimentId,
    variationKey,
    forceTargeting = true,
  }: SetForcedVariationParametersType): void {
    if (this.stubMode) {
      return;
    }
    KameleoonLogger.info`CALL: KameleoonClient.setForcedVariation(visitorCode: '${visitorCode}', experimentId: ${experimentId}, variationKey: '${variationKey}', forceTargeting: ${forceTargeting})`;

    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    this._setForcedVariation({
      visitorCode,
      experimentId,
      variationKey,
      forceTargeting,
    }).throw();

    KameleoonLogger.info`RETURN: KameleoonClient.setForcedVariation(visitorCode: '${visitorCode}', experimentId: ${experimentId}, variationKey: '${variationKey}', forceTargeting: ${forceTargeting})`;
  }

  public evaluateAudiences(visitorCode: string): void {
    if (this.stubMode) {
      return;
    }
    KameleoonLogger.info`CALL: KameleoonClient.evaluateAudiences(visitorCode: ${visitorCode})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    const targetedSegments: TargetedSegment[] = [];

    const targetingData = this.dataManager.getVisitorData(visitorCode);
    for (let segment of this.clientConfiguration.segments.values()) {
      if (!segment.audienceTracking) {
        continue;
      }

      const targetingResult = Utilities.checkTargeting({
        segment,
        visitorCode,
        targetingData,
        experimentId: null,
        clientConfiguration: this.clientConfiguration,
        packageInfo: this.externalPackageInfo,
        dataManager: this.dataManager,
        variationConfiguration: this.variationConfiguration,
      });

      const isTargeted = targetingResult.ok && targetingResult.data;

      if (isTargeted) {
        KameleoonLogger.info`Visitor ${visitorCode} has been targeted for ${segment}`;
        targetedSegments.push(new TargetedSegment(Number(segment.id)));
      }
    }

    if (targetedSegments.length !== 0) {
      this.dataManager.storeData(visitorCode, ...targetedSegments);
    }

    this.flush(visitorCode);

    KameleoonLogger.info`RETURN: KameleoonClient.evaluateAudiences(visitorCode: ${visitorCode})`;
  }

  protected setUserConsent({
    visitorCode,
    consent,
    setData,
  }: SetUserConsentParametersType): void {
    if (this.stubMode) {
      return;
    }
    KameleoonLogger.info`CALL: KameleoonClient.setUserConsent(visitorCode: ${visitorCode}, consent: ${consent}, setData: ${setData})`;
    this.visitorCodeManager.validateVisitorCode(visitorCode).throw();

    this.updateConsentData(visitorCode, consent);

    if (consent) {
      setData({
        visitorCode,
        key: KameleoonStorageKey.VisitorCode,
        maxAge: DEFAULT_MAX_AGE,
        path: PATH,
      });
    } else {
      if (this.visitorCodeManager.consentRequired) {
        setData({
          visitorCode: '',
          key: KameleoonStorageKey.VisitorCode,
          maxAge: ZERO_MAX_AGE,
          path: PATH,
        });
      }
    }

    KameleoonLogger.info`RETURN: KameleoonClient.setUserConsent(visitorCode: ${visitorCode}, consent: ${consent}, setData: ${setData})`;
  }

  private updateConsentData(visitorCode: string, consent: boolean): void {
    const readResult = this.consentDataStorage.read();
    if (!readResult.ok) {
      if (readResult.error.type === KameleoonException.StorageEmpty) {
        this.consentDataStorage.write({
          [visitorCode]: {
            consent,
          },
        });
      }
      return;
    }

    const data = readResult.data;
    data[visitorCode] = {
      consent,
    };
    this.consentDataStorage.write(data);
  }

  protected _isConsentProvided(visitorCode: string): boolean {
    KameleoonLogger.debug`CALL: KameleoonClient._isConsentProvided(visitorCode: ${visitorCode})`;
    const { isConsentRequired } = this.clientConfiguration;
    const consentDataResult = this.consentDataStorage.read();

    let isConsentProvided = false;

    if (!isConsentRequired) {
      isConsentProvided = true;
    } else if (consentDataResult.ok) {
      const consentData = consentDataResult.data[visitorCode];
      // for consistency with the old data in the storage
      if (typeof consentData === 'boolean') {
        isConsentProvided = consentData;
      } else {
        isConsentProvided = consentData && consentData.consent;
      }
    }

    KameleoonLogger.debug`RETURN: KameleoonClient._isConsentProvided(visitorCode: ${visitorCode}) -> (isConsentProvided: ${isConsentProvided})`;
    return isConsentProvided;
  }

  private _getFeatureVariables({
    visitorCode,
    featureKey,
    variationKey,
  }: GetFeatureVariablesParamsType): FeatureVariableType[] {
    KameleoonLogger.debug`CALL: KameleoonClient._getFeatureVariables(visitorCode: ${visitorCode}, featureKey: ${featureKey}, variationKey: ${variationKey})`;
    const featureFlags = this.clientConfiguration.featureFlags;
    const featureFlag = featureFlags.get(featureKey);

    if (!featureFlag) {
      throw new KameleoonError(
        KameleoonException.FeatureFlagConfigurationNotFound,
        featureKey,
      );
    }
    const variation = featureFlag.variations.find(
      (item) => item.key === variationKey,
    );

    if (!variation) {
      throw new KameleoonError(
        KameleoonException.FeatureFlagVariationNotFound,
        variationKey,
        visitorCode,
      );
    }

    KameleoonLogger.debug`RETURN: KameleoonClient._getFeatureVariables(visitorCode: ${visitorCode}, featureKey: ${featureKey}, variationKey: ${variationKey}) -> (variables: ${variation.variables})`;
    return variation.variables;
  }

  // @deprecated
  // To be removed with the deprecated methods
  private _getActiveFeatureVariations(
    visitorCode: string,
  ): FeatureFlagVariationType[] {
    KameleoonLogger.debug`CALL: KameleoonClient._getActiveFeatureVariations(visitorCode: ${visitorCode})`;
    const featureFlags = this.clientConfiguration.featureFlags;
    const activeVariations: FeatureFlagVariationType[] = [];

    for (const featureFlag of featureFlags.values()) {
      if (!featureFlag.environmentEnabled) {
        continue;
      }

      const evalExp = this._evaluate({
        visitorCode,
        featureFlag,
        track: false,
        save: false,
      });

      if (evalExp.variationKey !== OFF_VARIATION_KEY) {
        activeVariations.push({
          variationKey: evalExp.variationKey,
          variationId: evalExp.variationId,
          experimentId: evalExp.experimentId,
          featureFlagId: featureFlag.id,
          featureKey: featureFlag.featureKey,
          rule: null,
          isTargetedRule: evalExp.ruleType === RuleType.TARGETED_DELIVERY,
        });
      }
    }

    KameleoonLogger.debug`RETURN: KameleoonClient._getActiveFeatureVariations(visitorCode: ${visitorCode}) -> (activeVariations: ${activeVariations})`;
    return activeVariations;
  }

  private _evaluate({
    visitorCode,
    featureFlag,
    track,
    save,
  }: EvaluateParametersType): EvaluatedExperimentType {
    KameleoonLogger.debug`CALL: KameleoonClient._evaluate(visitorCode: ${visitorCode}, featureFlag: ${featureFlag}, track: ${track}, save: ${save})`;
    let evalExp: EvaluatedExperimentType;
    let forcedVariation: ForcedFeatureVariationType | null = null;
    const visitorData = this.dataManager.getVisitorData(visitorCode);

    const forcedVariationData =
      this.variationConfiguration.getForcedFeatureVariation(
        visitorCode,
        featureFlag.featureKey,
      );
    if (forcedVariationData.ok) {
      forcedVariation = forcedVariationData.data;
    }
    if (forcedVariation) {
      evalExp = KameleoonClient.EvaluatedExperiment.fromForcedFeatureVariation(
        featureFlag.defaultVariationKey,
        forcedVariation,
      );
    } else if (
      this._isVisitorNotInHoldout(
        visitorCode,
        track,
        save,
        featureFlag,
        visitorData,
      ) &&
      this._isFFUnrestrictedByMEGroup(visitorCode, featureFlag, visitorData)
    ) {
      const visitorIdentifier = this._getCodeForHash(
        visitorCode,
        featureFlag.bucketingCustomDataIndex,
        visitorData,
      );

      const variationData = this.variationConfiguration
        .getVariation({
          visitorCode,
          visitorIdentifier,
          featureFlag,
          track,
          withAssignment: save,
          targetingData: visitorData,
          clientConfiguration: this.clientConfiguration,
          dataManager: this.dataManager,
          packageInfo: this.externalPackageInfo,
        })
        .throw();
      evalExp =
        KameleoonClient.EvaluatedExperiment.fromVariationData(variationData);
    } else {
      evalExp = KameleoonClient.EvaluatedExperiment.fromDefaultVariationKey(
        featureFlag.defaultVariationKey,
      );
    }

    if (
      save &&
      !evalExp.isSimulated &&
      evalExp.experimentId !== null &&
      evalExp.variationId !== null
    ) {
      this.variationConfiguration.updateStoredVariations(visitorCode, {
        [evalExp.experimentId]: {
          variationId: evalExp.variationId,
          isTargetedRule: evalExp.ruleType === RuleType.TARGETED_DELIVERY,
          status: track ? TrackingStatus.Unsent : TrackingStatus.Sent,
        },
      });
    }

    this.eventManager.fireEvent(EventType.Evaluation, {
      featureKey: featureFlag.featureKey,
      variation: {
        key: evalExp.variationKey,
        experimentId: evalExp.experimentId,
        id: evalExp.variationId,
      },
    });

    KameleoonLogger.debug`RETURN: KameleoonClient._evaluate(visitorCode: ${visitorCode}, featureFlag: ${featureFlag}, track: ${track}, save: ${save}) -> (evalExp: ${evalExp})`;
    return evalExp;
  }

  private _isFFUnrestrictedByMEGroup(
    visitorCode: string,
    featureFlag: ConfigFeatureFlagType,
    visitorData?: KameleoonVisitorDataType,
  ): boolean {
    const meGroupName = featureFlag.mutuallyExclusiveGroup;
    if (!meGroupName) {
      return true;
    }
    KameleoonLogger.debug`CALL: KameleoonClient._isFFUnrestrictedByMEGroup(visitorCode: ${visitorCode}, featureFlag: ${featureFlag}, visitorData: ${visitorData})`;
    let selected = true;

    const meGroup = this.clientConfiguration.meGroups.get(meGroupName);
    if (meGroup) {
      const codeForHash = this._getCodeForHash(
        visitorCode,
        featureFlag.bucketingCustomDataIndex,
        visitorData,
      );

      const hashMEGroup = Hasher.getHashDoubleForMEGroup(
        codeForHash,
        meGroupName,
      );

      KameleoonLogger.debug`Calculated meGroup hash ${hashMEGroup} for code: ${codeForHash}, meGroup: ${meGroupName}`;

      selected = meGroup.getFeatureFlagByHash(hashMEGroup) === featureFlag;
    }

    KameleoonLogger.debug`RETURN: KameleoonClient._isFFUnrestrictedByMEGroup(visitorCode: ${visitorCode}, featureFlag: ${featureFlag}, visitorData: ${visitorData}) -> (selected: ${selected})`;
    return selected;
  }

  private _getFeatureVariation({
    visitorCode,
    featureKey,
    track,
  }: GetFeatureVariationParametersType): Result<VariationType, KameleoonError> {
    KameleoonLogger.debug`CALL: KameleoonClient._getFeatureVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey}, track: ${track})`;

    if (!this.initialized) {
      return Err(new KameleoonError(KameleoonException.Initialization));
    }

    const featureFlags = this.clientConfiguration.featureFlags;
    const featureFlag = featureFlags.get(featureKey);

    if (!featureFlag) {
      return Err(
        new KameleoonError(
          KameleoonException.FeatureFlagConfigurationNotFound,
          featureKey,
        ),
      );
    }

    if (!featureFlag.environmentEnabled) {
      return Err(
        new KameleoonError(
          KameleoonException.FeatureFlagEnvironmentDisabled,
          featureFlag.featureKey,
          this.environment,
        ),
      );
    }

    const evalExp = this._evaluate({
      visitorCode,
      featureFlag,
      track,
      save: true,
    });

    const { experimentId, variationId, variationKey, isSimulated } = evalExp;

    let resultVariables = new Map<string, KameleoonVariableType>();
    let hasJsCssVariables = false;

    try {
      const variables = this._getFeatureVariables({
        visitorCode,
        featureKey,
        variationKey,
      });

      variables.forEach((variable) => {
        if (
          !hasJsCssVariables &&
          (variable.type === VariableType.JS ||
            variable.type === VariableType.CSS)
        ) {
          hasJsCssVariables = true;
        }

        const parsedVariable: KameleoonVariableType =
          Parser.parseFeatureVariable(variable).throw();

        resultVariables.set(variable.key, parsedVariable);
      });
    } catch (_) {}

    if (experimentId && typeof variationId === 'number' && !isSimulated) {
      Utilities.updateCache({
        cacheManager: this.trackingCache,
        hasJsCssVariables,
        visitorCode,
        experimentId,
        variationId,
      });
    }

    if (track && !isSimulated) {
      this.tracker.scheduleVisitor(
        visitorCode,
        this._isConsentProvided(visitorCode),
      );
    }

    const resultData: VariationType = {
      key: variationKey,
      id: variationId,
      experimentId,
      variables: resultVariables,
    };

    KameleoonLogger.debug`RETURN: KameleoonClient._getFeatureVariation(visitorCode: ${visitorCode}, featureKey: ${featureKey}, track: ${track}) -> (variation: ${resultData})`;
    return Ok(resultData);
  }

  private _isVisitorNotInHoldout(
    visitorCode: string,
    track: boolean,
    save: boolean,
    featureFlag?: ConfigFeatureFlagType,
    visitorData?: KameleoonVisitorDataType,
  ): boolean {
    const holdout = this.clientConfiguration.holdout;
    if (!holdout) {
      return true;
    }
    KameleoonLogger.debug`CALL: KameleoonClient._isVisitorNotInHoldout(visitorCode: ${visitorCode}, track: ${track}, save: ${save}, featureFlag: ${featureFlag}, visitorData: ${visitorData})`;
    let isNotInHoldout = true;

    const codeForHash = this._getCodeForHash(
      visitorCode,
      featureFlag?.bucketingCustomDataIndex,
      visitorData,
    );

    const holdoutHash = Hasher.getHashDouble({
      visitorIdentifier: codeForHash,
      respoolTime: null,
      id: String(holdout.experimentId),
    });

    KameleoonLogger.debug`Calculated holdoutHash: ${holdoutHash} for visitorCode: ${codeForHash}`;

    let varByExp = Utilities.getVariationByHash(
      holdout.variationByExposition,
      holdoutHash,
    );

    if (varByExp !== null) {
      isNotInHoldout = varByExp.variationKey !== IN_HOLDOUT_VARIATION_KEY;
      if (save) {
        this.variationConfiguration.updateStoredVariations(visitorCode, {
          [holdout.experimentId]: {
            variationId: varByExp.variationId,
            isTargetedRule: false,
            status: track ? TrackingStatus.Unsent : TrackingStatus.Sent,
          },
        });
      }
    }

    KameleoonLogger.debug`RETURN: KameleoonClient._isVisitorNotInHoldout(visitorCode: ${visitorCode}, track: ${track}, save: ${save}, featureFlag: ${featureFlag}, visitorData: ${visitorData}) -> (isNotInHoldout: ${isNotInHoldout})`;
    return isNotInHoldout;
  }

  private _setForcedVariation({
    visitorCode,
    experimentId,
    variationKey,
    forceTargeting = true,
  }: SetForcedVariationParametersType): Result<void, KameleoonError> {
    KameleoonLogger.debug`CALL: KameleoonClient._setForcedVariation(visitorCode: '${visitorCode}', experimentId: ${experimentId}, variationKey: '${variationKey}', forceTargeting: ${forceTargeting})`;

    if (variationKey === null) {
      const writeResult =
        this.variationConfiguration.updateForcedExperimentVariation(
          visitorCode,
          experimentId,
          null,
        );
      KameleoonLogger.debug`RETURN: KameleoonClient._setForcedVariation(visitorCode: '${visitorCode}', experimentId: ${experimentId}, variationKey: '${variationKey}', forceTargeting: ${forceTargeting}) -> (writeResult: ${writeResult})`;
      return writeResult;
    }

    let rule = null;
    let varByExp = null;

    rule = this.clientConfiguration.experimentMap.get(experimentId);
    if (!rule) {
      KameleoonLogger.error`Experiment with id ${experimentId} not found for visitor ${visitorCode}`;
      return Err(
        new KameleoonError(
          KameleoonException.FeatureFlagExperimentNotFound,
          experimentId,
          visitorCode,
        ),
      );
    }
    varByExp = rule.variations.get(variationKey!);
    if (varByExp === undefined) {
      KameleoonLogger.error`Variation with key ${variationKey} not found in experiment ${rule} for visitor ${visitorCode}`;
      return Err(
        new KameleoonError(
          KameleoonException.FeatureFlagVariationNotFound,
          variationKey!,
          visitorCode,
        ),
      );
    }

    rule = {
      type: rule.type,
      experimentId: experimentId,
    } as ForcedVariationRuleType;
    varByExp = {
      variationKey: variationKey,
      variationId: varByExp,
    } as ForcedVariationVarByExpType;

    const writeResult =
      this.variationConfiguration.updateForcedExperimentVariation(
        visitorCode,
        experimentId,
        {
          rule: rule,
          varByExp: varByExp,
          forceTargeting,
        } as ForcedExperimentVariationType,
      );

    KameleoonLogger.debug`RETURN: KameleoonClient._setForcedVariation(visitorCode: '${visitorCode}', experimentId: ${experimentId}, variationKey: '${variationKey}', forceTargeting: ${forceTargeting}) -> (writeResult: ${writeResult})`;
    return writeResult;
  }

  private _getCodeForHash(
    visitorCode: string,
    bucketingCustomDataIndex?: number | null,
    visitorData?: KameleoonVisitorDataType,
  ): string {
    KameleoonLogger.debug`CALL: KameleoonClient._getCodeForHash(visitorCode: ${visitorCode}, bucketingCustomDataIndex: ${bucketingCustomDataIndex}, visitorData: ${visitorData})`;
    let codeForHash;
    if (bucketingCustomDataIndex != null) {
      const customData = visitorData?.customData;
      if (customData) {
        const customDataValue = customData[bucketingCustomDataIndex]?.value[0];
        if (customDataValue) {
          codeForHash = customDataValue;
        }
      }
    }

    if (!codeForHash) {
      codeForHash = this.dataManager.getVisitorIdentifier(visitorCode);
    }

    KameleoonLogger.debug`RETURN: KameleoonClient._getCodeForHash(visitorCode: ${visitorCode}, bucketingCustomDataIndex: ${bucketingCustomDataIndex}, visitorData: ${visitorData}) -> (codeForHash: ${codeForHash})`;
    return codeForHash;
  }

  private static EvaluatedExperiment = class {
    static fromForcedFeatureVariation(
      defaultVariationKey: string,
      forcedFeatureVariation: ForcedFeatureVariationType,
    ): EvaluatedExperimentType {
      let varKey = defaultVariationKey;
      if (forcedFeatureVariation.varByExp) {
        varKey = forcedFeatureVariation.varByExp.variationKey;
      } else if (
        forcedFeatureVariation.rule &&
        forcedFeatureVariation.rule.type === RuleType.EXPERIMENTATION
      ) {
        varKey = OFF_VARIATION_KEY;
      }
      return {
        variationKey: varKey,
        variationId: forcedFeatureVariation.varByExp?.variationId || null,
        experimentId: forcedFeatureVariation.rule?.experimentId || null,
        ruleType: forcedFeatureVariation.rule?.type || null,
        isSimulated: forcedFeatureVariation.simulated,
      };
    }

    static fromVariationData(
      variationData: FeatureFlagVariationType,
    ): EvaluatedExperimentType {
      return {
        variationKey: variationData.variationKey,
        variationId: variationData.variationId,
        experimentId: variationData.experimentId,
        ruleType: variationData.rule?.type ?? null,
        isSimulated: false,
      };
    }

    static fromDefaultVariationKey(
      defaultVariationKey: string,
    ): EvaluatedExperimentType {
      return {
        variationKey: defaultVariationKey,
        variationId: null,
        experimentId: null,
        ruleType: null,
        isSimulated: false,
      };
    }
  };
}
